//=============================================================================
//                       <--- Developed By Icon Team ---> 18 January 2021
//=============================================================================

// npm packages
import React, { useState, useEffect, useContext } from 'react';
import context from 'context/context';
import { useHistory } from 'react-router-dom';
import * as moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import InputAdornment from "@material-ui/core/InputAdornment";

// custom pages
import Table from 'components/Table/Table';
import StackingPromotion from 'apis/StackingPromotion/StackingPromotion';
import { styles } from '../Promotion/Criteria/styles';
import Loader from 'components/Loader/Loader';
import VisibilityIcon from '@material-ui/icons/Visibility';
import useDebounce from "helpers/useDebounce";

// material-ui
import Button from '@material-ui/core/Button';
import TextField from "@material-ui/core/TextField";
import Box from '@material-ui/core/Box';
import Grid from "@material-ui/core/Grid";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import { withStyles } from "@material-ui/core/styles";
import searchIcon from "assets/img/Search_Icon.png";

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': { color: '##D3D3D3' },
    '& .MuiFormLabel-root': { color: '#707070' },
    '& .MuiInputBase-input': { color: '#707070' },
    '& .MuiInput-underline:before': { borderBottomColor: '#707070!important' },
    '& .MuiInput-underline:after': { borderBottomColor: '#707070!important' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#707070' },
      '&:hover fieldset': { borderColor: '#707070' },
      '&.Mui-focused fieldset': { borderColor: '#707070' },
      '&.MuiOutlinedInput-root': {
        marginTop: -11,
        borderRadius: 20,
        background: '#ededed',
        display: 'flex',
        marginLeft: 30,
        marginRight: 10,
      },
    },
  },
})(TextField);


export default function StackingPromotions() {
  const { dispatch } = useContext(context);

  // getStackingPromotion (get-promo) -> used for the plain page load / pagination
  // search (search)                  -> used only while the user is actively searching
  const { getStackingPromotion, search } = StackingPromotion();

  const history = useHistory();
  const classes = styles();
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [offset, setOffset] = useState(1);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [exporting, setExporting] = useState(false);
  const searchBounceValues = useDebounce(searchValue);

  //===========================================================================
  // Breadcrumb
  //===========================================================================

  useEffect(() => {
    dispatch({
      type: 'SET_PATHNAME',
      payload: ['HOME', 'ADMIN', 'Promotion Management', 'Criteria'],
    });
  }, [dispatch]);


  //===========================================================================
  // Load stack-head records
  // - No search term  -> GET /get-promo   (getStackingPromotion)
  // - Has search term -> GET /search      (search)
  //===========================================================================

  const loadStacks = async (
    searchTerm = "",
    page = offset
  ) => {
    setLoaded(false);
    try {
      const trimmedTerm = (searchTerm || "").trim();

      const values = {
        offset: (page - 1) * 10,
        limit: 10,
        searchValue: trimmedTerm,
      };

      const res = trimmedTerm.length > 0
        ? await search(values)
        : await getStackingPromotion(values);

      if (res?.success) {
        setData(res.data || []);
        // getStackingPromotion returns `count`; search returns pagination.totalRecords (and count)
        setCount(res?.pagination?.totalRecords ?? res.count ?? 0);
      } else {
        setData([]);
        setCount(0);
      }

      setLoaded(true);
    } catch (e) {
      console.log({ e });
      setData([]);
      setCount(0);
      setLoaded(true);
    }
  };

  // Initial page load + whenever the page changes (no active search term case
  // is handled inside loadStacks based on searchValue passed in)
  useEffect(() => {
    loadStacks(searchValue, offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  // Debounced search-as-you-type — this is what triggers the /search endpoint
  useEffect(() => {
    setOffset(1);
    loadStacks(searchBounceValues, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBounceValues]);


  //===========================================================================
  // Pagination
  //===========================================================================

  const handlePagination = (value) => {
    setOffset(value);
  };


  //===========================================================================
  // Export
  //===========================================================================

  const exportRows = data.map((d, i) => ({
    s_no: i + 1,
    promotion_name: d?.promotion_name,
    promotion_description: d?.promotion_description,
    type:d?.type,
    multiplier_sum: d?.sum_of_multipliers ?? 0,
    status: (d?.status === "Active" || d?.is_active) ? "Active" : "Inactive",
  }));


  return (
    <React.Fragment>
      <Grid container className={classes.root}>

        <Grid item xs={12} sm={6} lg={6}>
          <Button
            className={`${classes.createBtn} dash-1-filter-btn`}
            onClick={() => history.push('/dashboard/create-stack-promo')}
          >
            {t('Create')}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} lg={6} style={{ textAlign: 'end', paddingTop: '10px' }}>

          <CssTextField
            placeholder="Search promotion name or description"
            type="text"
            variant="outlined"
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchValue.length > 0 ? (
                    <CloseOutlinedIcon
                      className={classes.searchInputIcon}
                      onClick={() => {
                        setSearchValue("");
                        setOffset(1);
                        // cleared -> back to /get-promo
                        loadStacks("", 1);
                      }}
                    />
                  ) : (
                    <img src={searchIcon} alt="" width="20px" className={classes.searchInputIcon} />
                  )}
                </InputAdornment>
              ),
            }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          <CSVLink
            id="csv"
            data={exportRows}
            headers={[
              { label: "S.No", key: "s_no" },
              { label: "Promotion Name", key: "promotion_name" },
              { label: "Promotion Description", key: "promotion_description" },
              { label: "Type", key: "type" },
              { label: "Sum of Multipliers", key: "multiplier_sum" },
              { label: "Status", key: "status" },
            ]}
            filename={`Promotion_Stacks_` + moment(new Date()).format("MM-DD-YYYY,h:mm A") + ".csv"}
            className={classes.CSVlink}
          />

          <button
            disabled={exporting}
            className={classes.exportBtn}
            onClick={() => {
              setExporting(true);
              document.getElementById("csv").click();
              setExporting(false);
            }}
          >
            Export
          </button>

        </Grid>
      </Grid>


      <Grid className={classes.tableDownInResponsive}>
        <Grid item xs={12} sm={12} lg={12}>
          {loaded ? (
            <Table
              headings={[
                'S.No',
                'Promotion Name',
                'Promotion Description',
                'Type',
                'Sum of Multipliers',
                'Status',
                'View',
              ]}
              data={
                data &&
                data.map((d, i) => [
                  (offset - 1) * 10 + i + 1,
                  d?.promotion_name || '-',
                  d?.promotion_description || '-',
                  d?.type || '-',
                  d?.multiplier_display ?? `${d?.sum_of_multipliers ?? 0}X`,
                  (d?.status === "Active" || d?.is_active) ? (
                    <span style={{ color: 'green' }}>{t("Active")}</span>
                  ) : (
                    <span style={{ color: 'red' }}>{t("Inactive")}</span>
                  ),
                  <Box
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      history.push('/dashboard/edit-stack-promo', {
                        promotionId: d?.promotion_id,
                        type: d?.type,
                      });
                    }}
                  >
                    <VisibilityIcon />
                  </Box>,
                ])
              }
              offset={offset}
              count={
                (count / 10) % 1 === 0
                  ? Math.floor(count / 10)
                  : Math.floor(count / 10 + 1)
              }
              handlePagination={handlePagination}
            />
          ) : (
            <Loader Loading={true} top={'15%'} />
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
}